import { IconDownloadMediumOutline, IconMailMediumOutline } from '@teamleader/ahoy';
import { translate, Translation } from '@teamleader/i18n';
import fileDownload from 'js-file-download';
import React, { useCallback, useContext, useRef, useState } from 'react';

import InvoiceName from './InvoiceName';
import InvoicesHeaderData from './InvoicesHeaderData';
import Header from '..';
import { useIsBase, useIsLarge, useIsMedium, useIsSmall } from '../../../../Components/Context/Hooks/mediaQueries';
import Box, { BoxProps } from '../../../../Components/UI/Box';
import Button from '../../../../Components/UI/Button';
import IconButton from '../../../../Components/UI/IconButton';
import Link from '../../../../Components/UI/Link';
import Spinner from '../../../../Components/UI/Spinner';
import { WithTooltip } from '../../../../Components/UI/Tooltip';
import { DownloadWithCreditNotes } from '../../../../Types/api';
import { DOWNLOAD_STATE } from '../../../../Types/downloadState';
import { MENUS } from '../../../../Utils/Menus';
import { EVENTS, track } from '../../../../Utils/analytics';
import { getCreditNoteName, getInvoiceName } from '../../../../Utils/invoiceName';
import { captureException } from '../../../../Utils/sentry';
import { sanitizeString } from '../../../../Utils/stringManipulation';
import useGlobalContext, { InvoiceContext } from '../../Context';
import styles from '../styles.css';

const INVOICE_NUMBER_PLACEHOLDER = '%number%';

const ButtonWithTooltip = WithTooltip(Button);

const InvoicesHeader: React.FC<BoxProps> = ({ ...rest }) => {
  const { request, handleOpenMenu, openMenu, paymentServiceProvidersEnabled, handleAddBanner } = useGlobalContext();
  const { invoice, invoicePdf, department, madeTooManyPaymentAttempts } = useContext(InvoiceContext);
  const isLarge = useIsLarge();
  const isMedium = useIsMedium();
  const isSmall = useIsSmall();
  const isBase = useIsBase();
  const hasDueAmount = !!invoice && invoice.total.due.amount > 0;
  const previousBannerDismissFunction = useRef<() => void>();
  const [downloadState, setDownloadState] = useState(DOWNLOAD_STATE.IDLE);

  const handleDownload = useCallback(() => {
    (async () => {
      setDownloadState(DOWNLOAD_STATE.PROCESSING);

      if (previousBannerDismissFunction.current) {
        previousBannerDismissFunction.current();
      }

      // The browser will take care of most invalid characters, but some edge cases were not covered.
      // e.g. in some cases, a filename with the ® character did not work and the browser just used "download" as file name.
      // By sanitizing this document name first, we support these edge cases as well
      const documentName = sanitizeString(
        `${getInvoiceName(invoice!.invoiceNumber.replaceAll('/', '-'), invoice!.client.language)} - ${
          department!.name
        }`,
      );

      let data;

      if (invoice!.creditNotes.length > 0) {
        try {
          data = await request!<DownloadWithCreditNotes>('/invoices.downloadWithCreditNotes', {
            body: JSON.stringify({
              id: invoice!.id,
              invoiceFilenameTemplate: `${getInvoiceName(INVOICE_NUMBER_PLACEHOLDER, invoice!.client.language)} - ${
                department!.name
              }.pdf`,
              creditNoteFilenameTemplate: `${getCreditNoteName(
                INVOICE_NUMBER_PLACEHOLDER,
                invoice!.client.language,
              )} - ${department!.name}.pdf`,
            }),
          });
        } catch (error) {
          captureException(error);
          setDownloadState(DOWNLOAD_STATE.FAIL);

          previousBannerDismissFunction.current = handleAddBanner!(
            {
              label: (
                <Translation
                  id="downloadError"
                  values={{
                    link: (chunks: React.ReactNode[]): React.ReactNode => (
                      <Link onClick={handleDownload} className={styles['retry']}>
                        {chunks}
                      </Link>
                    ),
                  }}
                />
              ),
              variant: 'error',
            },
            true,
          );

          return;
        }

        fileDownload(data, `${documentName}.zip`, 'application/zip');
      } else {
        data = await invoicePdf!.getData();

        fileDownload(data, `${documentName}.pdf`, 'application/pdf');
      }

      track(EVENTS.DOWNLOADED_INVOICE, { invoice: invoice!.id });

      setDownloadState(DOWNLOAD_STATE.SUCCESS);

      await request!('/invoices.markAsDownloaded', {
        body: JSON.stringify({
          id: invoice!.id,
        }),
      });
    })();
  }, [invoice, department, request, handleAddBanner, invoicePdf]);

  const handleSendFeedback = useCallback(() => {
    handleOpenMenu!(MENUS.FEEDBACK);
    track(EVENTS.STARTED_ADDING_INVOICE_FEEDBACK);
  }, [handleOpenMenu]);

  const handlePay = useCallback(() => {
    handleOpenMenu!(MENUS.PAYMENT);
    track(EVENTS.STARTED_PAYING, { fromHeader: true });
  }, [handleOpenMenu]);

  return (
    <>
      <Header {...rest}>
        <Box className={styles['title-wrapper']}>
          <InvoiceName />
        </Box>
        <Box display="flex" alignItems="flex-start" marginVertical={3} marginLeft={3}>
          {invoice && department && isMedium && <InvoicesHeaderData invoice={invoice} department={department} />}
          {downloadState === DOWNLOAD_STATE.PROCESSING ? (
            <IconButton icon={<Spinner />} disabled marginLeft={4} />
          ) : (
            <IconButton
              icon={<IconDownloadMediumOutline />}
              onClick={handleDownload}
              title={translate('actions.download')}
              disabled={!invoicePdf}
              marginLeft={4}
            />
          )}

          {!isLarge && (
            <IconButton
              marginLeft={3}
              icon={<IconMailMediumOutline />}
              onClick={handleSendFeedback}
              title={translate('actions.sendEmail')}
              disabled={!invoicePdf}
            />
          )}
          {isLarge && (
            <Button disabled={!invoicePdf} onClick={handleSendFeedback} marginLeft={4}>
              {translate('actions.sendEmail')}
            </Button>
          )}
          {!isBase && paymentServiceProvidersEnabled && hasDueAmount && (
            <Box marginLeft={4}>
              <ButtonWithTooltip
                disabled={!invoicePdf || openMenu?.menu === MENUS.PAYMENT}
                level="primary"
                label={
                  madeTooManyPaymentAttempts
                    ? translate('tooltips.tooManyAttempts')
                    : openMenu?.menu === MENUS.PAYMENT
                    ? translate('tooltips.selectMethod')
                    : undefined
                }
                placement="left"
                onClick={handlePay}
              >
                {translate('actions.payInvoice')}
              </ButtonWithTooltip>
            </Box>
          )}
        </Box>
      </Header>
      {invoice && department && !isMedium && !isSmall && (
        <Box className={styles['data-holder']}>
          <InvoicesHeaderData invoice={invoice} department={department} />
        </Box>
      )}
    </>
  );
};

export default InvoicesHeader;
